import clsx from "clsx";
import React, { useEffect, useRef } from "react";
import { usePageManager } from "web/react/components/feeds-filters/feeds-filters-pagination/pagination-state";
import { FilterManager } from "web/react/components/feeds-filters/hooks/use-filter-manager";
import { FilterAppliedFilters } from "web/react/components/filter-applied-filters/filter-applied-filters";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import { HStack } from "web/react/emo/h-stack";
import { Heading } from "web/react/emo/heading";
import { Text } from "web/react/emo/text";
import { View } from "web/react/emo/view";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import { UniversalFiltersData } from "web/types/universal-filters";
import * as styles from "./collapsable.css";

interface CollapsableProps {
    isOpen: boolean;
    sectionTitle: string;
    filterCount: number;
    children: JSX.Element | null;
    closeCollapsable(): void;
    containerRef: React.RefObject<HTMLDivElement> | null;
    filters: UniversalFiltersData;
    productCount: number;
    onClearClick(): void;
    isLoading: boolean;
    onClick: FilterManager["onClick"];
    selectedFilterInfo: FilterManager["selectedFilterInfo"];
}

export function Collapsable({
    isOpen,
    children,
    sectionTitle,
    filterCount,
    closeCollapsable,
    containerRef,
    productCount,
    onClearClick,
    isLoading,
    selectedFilterInfo,
    onClick,
}: CollapsableProps): JSX.Element | null {
    const pageManager = usePageManager(gettext("general.filters"));
    const { paginationState, popPage } = pageManager;
    const collapsableRef = useRef<HTMLDivElement>(null);
    const stickyBarFlag = String(environment.getFeature("act_sticky_feed_filters"));
    const pageSubType = environment.get("pageSubType");

    useEffect(() => {
        /* check if click is within the containerRef (the curved filter buttons)
         * or inside the collapsableRect a rectangle of collapsableWrapper.
         * if click is outside these we can assume to close the filter collapsable.
         */
        function handleClick(e): void {
            const collapsableRect = collapsableRef.current?.getBoundingClientRect();
            if (!collapsableRect || containerRef?.current?.contains(e.target)) {
                return;
            }
            const x = e.clientX;
            if (x < collapsableRect.left || x >= collapsableRect.right) closeCollapsable();
            const y = e.clientY;
            if (y < collapsableRect.top || y >= collapsableRect.bottom) closeCollapsable();
            return;
        }
        document.addEventListener("click", handleClick);
        return () => document.removeEventListener("click", handleClick);
    }, [containerRef, isOpen, closeCollapsable]);

    if (!isOpen) return null;

    return (
        <>
            <div
                className={styles.collapsableOverlay}
                style={
                    stickyBarFlag !== "control" && pageSubType !== "wishlist"
                        ? { height: "100vh" }
                        : {}
                }
                onClick={closeCollapsable}
            />
            <div ref={collapsableRef} className={styles.collapsableWrapper}>
                {isLoading && (
                    <div className={styles.spinner}>
                        <LoadingSpinner />
                    </div>
                )}
                <div className={styles.collapsableContainerContent}>
                    <View width={"full"}>
                        <HStack spacing="xxxs">
                            <Heading textStyle={"subhead"}>{sectionTitle}</Heading>
                            <Text
                                className={styles.appliedFilterCount}
                                as={"span"}
                                textStyle={"body-2"}
                            >
                                {`(${filterCount})`}
                            </Text>
                            <button
                                data-testid="clear-more-filters-button"
                                onClick={() => {
                                    filterCount > 0 && onClearClick();
                                }}
                            >
                                <Text
                                    textStyle="footnote"
                                    className={clsx(styles.clearButton, {
                                        [styles.clearButtonDisabled]: filterCount === 0,
                                    })}
                                >
                                    {
                                        // "Clear"
                                        gettext("feed.filters.clear_btn_text")
                                    }
                                </Text>
                            </button>
                        </HStack>
                        {children}
                    </View>
                </div>
                <FilterAppliedFilters
                    onClose={popPage}
                    onClick={onClick}
                    selectedFilterInfo={selectedFilterInfo}
                    paginationState={paginationState}
                    count={productCount}
                />
            </div>
        </>
    );
}
